import React from 'react'
import { Button, CustomersCardRadioInput, Pill } from '@demandlocal/components'
import { getImageURL } from '../../lib/utils/getImageURL'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { ReactComponent as LeadsIcon } from '../../images/icons/audience_people.svg'
import { ReactComponent as IconClose } from '../../images/icons/icon_close.svg'
import { ReactComponent as ELeadsLogo } from '../../images/vendors/eleads_logo.svg'
import { ReactComponent as DealerVaultLogo } from '../../images/vendors/dealer_vault_icon.svg'
import { ReactComponent as TekionLogo } from '../../images/vendors/tekion_logo.svg'
import { ReactComponent as VehicleIcon } from '../../images/icons/icon_car.svg'
import { ReactComponent as WrenchIcon } from '../../images/icons/icon_service.svg'
import { ReactComponent as ServerIcon } from '../../images/icons/icon_server.svg'
import { ReactComponent as HashedEmailIcon } from '../../images/icons/icon_hashed email.svg'
import SourceLogo from '../../components/Sources/SourceLogo'
import { useDealership } from '../../store/zustand/dealershipStore'
import { useTranslation } from 'react-i18next'

type FieldValues = {
  customers_type: 'sales' | 'leads' | 'service' | 'tag'
}
export default function AddCustomersPicker(): JSX.Element {
  const formContext = useForm<FieldValues>({
    mode: 'onSubmit',
  })
  const navigate = useNavigate()
  const dealership = useDealership()
  const { t } = useTranslation('routes')

  const {
    handleSubmit,
    formState: { isDirty },
  } = formContext

  const onSubmit = (values: {
    customers_type: 'sales' | 'leads' | 'service' | 'tag'
  }): void => {
    const { customers_type } = values
    navigate(customers_type)
  }
  return (
    <FormProvider {...formContext}>
      <form onSubmit={handleSubmit(onSubmit)} className={'flex flex-1 flex-col h-screen'}>
        <div className={'py-10 flex justify-between items-center px-12'}>
          <h1 className={' text-3xl font-bold'}>Import Customers</h1>
          <IconClose
            className={'cursor-pointer'}
            onClick={(e) => {
              navigate(t('dealership.customers.sources.list', { dealership }))
            }}
          />
        </div>
        <div className="flex flex-row px-12 gap-5 flex-1">
          <div className="flex justify-start w-1/2">
            <span className={'text-primary-xiketic text-base pb-2 flex-wrap w-[340px]'}>
              Connect your customer sources and data from leading CRM and DMS
            </span>
          </div>
          <div className="flex flex-row flex-wrap items-center gap-3 w-1/2">
            <SourceLogo icon={<ELeadsLogo />} />
            <SourceLogo icon={<TekionLogo />} />
            <SourceLogo
              icon={<img src={getImageURL('images/vinsolutions_logo.png')} />}
            />
            <SourceLogo icon={<DealerVaultLogo />} />
            <SourceLogo
              icon={<img src={getImageURL('images/dealer_socket_logo.png')} />}
            />
            <SourceLogo
              icon={<img src={getImageURL('images/vendors/drive_centric_icon.png')} />}
            />
          </div>
        </div>

        <div className={'h-full overflow-auto py-2 flex flex-col gap-10'}>
          <div className={'flex-1 flex-col flex justify-center px-12'}>
            <div className={'grid grid-cols-3 gap-5'}>
              <CustomersCardRadioInput
                key={'sales'}
                name={'customers_type'}
                value={'sales'}
                icon={<LeadsIcon className="stroke-primary-persian-blue" />}
                descriptionContent={
                  <div className="flex flex-col justify-between items-center gap-3">
                    <span className="text-base font-bold text-center">
                      Import Sale Customers
                    </span>
                    <span className="text-sm font-medium text-center">
                      People who purchased from you. Purchase date and VIN are required.
                    </span>
                    <div>
                      <Pill type="success">Pre built integrations</Pill>
                    </div>
                  </div>
                }
              />
              <CustomersCardRadioInput
                key={'leads'}
                name={'customers_type'}
                value={'leads'}
                icon={<VehicleIcon className="stroke-primary-persian-blue" />}
                descriptionContent={
                  <div className="flex flex-col justify-between items-center gap-3 flex-1">
                    <span className="text-base font-bold text-center">
                      Import Customer Leads
                    </span>
                    <span className="text-sm font-medium text-center">
                      People who submitted a lead. Lead date and email or phone are
                      required.
                    </span>
                    <div>
                      <Pill type="success">Pre built integrations</Pill>
                    </div>
                  </div>
                }
              />
              <CustomersCardRadioInput
                name={'customers_type'}
                value={'service'}
                icon={<WrenchIcon className="stroke-primary-persian-blue stroke-0" />}
                descriptionContent={
                  <div className="flex w-100">
                    <div className="flex flex-col justify-between items-center gap-3 flex-1">
                      <span className="text-base font-bold text-center">
                        Service Customers
                      </span>
                      <span className="text-sm font-medium text-center">
                        Import your service and parts customers. Service date required.
                      </span>
                      <div>
                        <Pill type="success">Pre built integrations</Pill>
                      </div>
                    </div>
                  </div>
                }
              />
              <CustomersCardRadioInput
                name={'customers_type'}
                value={'other'}
                icon={<ServerIcon className="stroke-primary-persian-blue" />}
                descriptionContent={
                  <div className="flex flex-col justify-center items-center gap-3">
                    <span className="text-base font-bold text-center">
                      Import any offline data source
                    </span>
                    <span className="text-sm font-medium text-center">
                      Import any list of customers or contacts with an email or phone
                    </span>
                  </div>
                }
              />
              <CustomersCardRadioInput
                name={'customers_type'}
                value={'hashed-customers'}
                icon={<HashedEmailIcon className="stroke-primary-persian-blue" />}
                descriptionContent={
                  <div className="flex flex-col justify-center items-center gap-3">
                    <span className="text-base font-bold text-center">
                      Import SHA-2 Hashed Emails
                    </span>
                    <span className="text-sm font-medium text-center">
                      Import any list of hashed emails, ready for targeting on Google,
                      Facebook and theTradeDesk
                    </span>
                  </div>
                }
              />
              <CustomersCardRadioInput
                name={'customers_type'}
                value={'opt-out-customers'}
                icon={<LeadsIcon className="stroke-primary-persian-blue stroke-0" />}
                descriptionContent={
                  <div className="flex flex-col justify-center items-center gap-3">
                    <span className="text-base font-bold text-center">
                      Opt-out Customers
                    </span>
                    <span className="text-sm font-medium text-center">
                      Import your opt-out customers to exclude them from your campaigns or
                      audience lists.
                    </span>
                  </div>
                }
              />
            </div>
          </div>
        </div>
        <div className={'flex justify-between py-5 border-t px-12'}>
          <Button
            variant={'tertiary'}
            onClick={(e) => {
              e.preventDefault()
              navigate(t('dealership.customers.sources.list', { dealership }))
            }}>
            Cancel
          </Button>
          <Button disabled={!isDirty} variant={'primary'}>
            Continue
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
